import { DependencyList, useEffect, useRef } from 'react';
import { useDialog } from '@/hooks/useDialog';
import type {
  DialogId,
  DialogOptions,
} from '@/components/ui/shared/Dialog/types';

/*
 * Bridges a component-owned visibility boolean onto the shared Dialog stack:
 * shows on true, refreshes the dialog in place when its inputs change, removes
 * it on false. Lets state/prop-driven callers keep their existing trigger flow
 * while rendering through the shared Dialog.
 */
export function useDialogBridge(
  isVisible: boolean,
  getOptions: () => DialogOptions,
  deps: DependencyList,
) {
  const { showDialog, updateDialog, removeDialog } = useDialog();
  const idRef = useRef<DialogId | null>(null);

  useEffect(() => {
    if (isVisible) {
      if (idRef.current !== null) {
        updateDialog(idRef.current, getOptions());
      } else {
        idRef.current = showDialog(getOptions());
      }
    } else if (idRef.current !== null) {
      removeDialog(idRef.current);
      idRef.current = null;
    }
    // getOptions is rebuilt each render; deps capture the values it reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, ...deps]);

  // On unmount, drop our dialog so it isn't orphaned when the owner leaves the
  // tree while still visible (e.g. a screen that early-returns the host).
  useEffect(() => {
    return () => {
      if (idRef.current !== null) {
        removeDialog(idRef.current);
        idRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
